
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';

// --- Models ---
import { AdhocMaintenanceItem } from '@/models/AdhocMaintenance.model';
import { Vehicle } from '@/models/Vehicle.model';

// --- Services ---
import AppService from '@/services/app';
import VehicleService from '@/services/vehicle';
import AdhocMaintenanceService from '@/services/adhocMaintenance';

// --- Third Party ---
import dayjs from 'dayjs';

@Component
export default class AdhocMaintenanceListing extends Vue {
  // used by Filter Area
  search: string = '';

  // used by No Data Slot
  isLoading: boolean = true;

  // used by Item Dialog for Create/Update
  showItemDialog: boolean = false;
  dateReportedDatePicker: boolean = false;
  title: string = '';
  clickedItem: AdhocMaintenanceItem = {
    id: 0,
    dateReported: '',
    vehicle1Id: 0,
    vehicle1Code: '',
    vehicle1TypeId: 0,
    vehicle1Type: '',
    description: '',
  };

  activeVehicles: Vehicle[] = [];
  selectedVehicle1: Vehicle = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
  selectedVehicle2: Vehicle = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
  disableLinkedTrailer: boolean = false;

  isUpdate: boolean = false;
  valid: boolean = false;
  clicked: boolean = false;

  rules = {
    required: (value: string) => !!value || '',
    requiredObject: (value: any) => value.id > 0,
  };

  // used by Confirm Action Dialog for Delete
  action: string = '';
  dialogContent: string = '';
  showConfirmActionDialog: boolean = false;
  confirmActionDelay: boolean = false;

  // #region Functions to display list data on page load
    headers() {
      return [
        {
          text: '',
          value: 'hasJobCard',
        },
        {
          text: 'Date Reported',
          value: 'dateReported',
        },
        {
          text: 'Vehicle',
          value: 'vehicleCodes',
        },        
        {
          sortable: false,
          text: 'Description',
          value: 'description',
        },
        {
          text: 'Completion ETA',
          value: 'completionEta',
        },
        {
          sortable: false,
          text: '',
          value: 'action',
          align: 'end',
        },
      ];
    }

    customFilter(column: string, search: string, item: AdhocMaintenanceItem) {
      let result: boolean = false;

      if (search === '') {
        result = true;
      } else {
        result = item.dateReported.includes(search) ||
          item.vehicle1Code.toLowerCase().includes(search.toLowerCase()) ||
          (item.vehicle2Code ? item.vehicle2Code.toLowerCase().includes(search.toLowerCase()) : false) ||
          item.description.toLowerCase().includes(search.toLowerCase()) ||
          (item.completionEta ? item.completionEta.includes(search) : false);
      }

      return result;
    }

    customSort(items: any[], sortBy: string[], sortDesc: boolean[], locale: string) {
      // If the result is negative, a is sorted before b.
      // If the result is positive, b is sorted before a.
      // If the result is 0, no changes are done with the sort order of the two values.

      switch (sortBy[0]) {
        case 'hasJobCard':
          // hasJobCard is a boolean
          if (sortDesc[0]) {
            items.sort((a: AdhocMaintenanceItem, b: AdhocMaintenanceItem) => (a.jobCardId ? '1' : '0').localeCompare((b.jobCardId ? '1' : '0')));
          } else {
            items.sort((a: AdhocMaintenanceItem, b: AdhocMaintenanceItem) => (b.jobCardId ? '1' : '0').localeCompare((a.jobCardId ? '1' : '0')));
          }
          break;
        
        case 'dateReported':
          if (sortDesc[0]) {
            items.sort((a: AdhocMaintenanceItem, b: AdhocMaintenanceItem) => a.dateReported.localeCompare(b.dateReported));
          } else {
            items.sort((a: AdhocMaintenanceItem, b: AdhocMaintenanceItem) => b.dateReported.localeCompare(a.dateReported));
          }
          break;

        case 'vehicleCodes':
          if (sortDesc[0]) {
            items.sort((a: AdhocMaintenanceItem, b: AdhocMaintenanceItem) => a.vehicle1Code.toLowerCase().localeCompare(b.vehicle1Code.toLowerCase()));
          } else {
            items.sort((a: AdhocMaintenanceItem, b: AdhocMaintenanceItem) => b.vehicle1Code.toLowerCase().localeCompare(a.vehicle1Code.toLowerCase()));
          }
          break;

        case 'completionEta':
          if (sortDesc[0]) {
            items.sort((a: AdhocMaintenanceItem, b: AdhocMaintenanceItem) => (a.completionEta ? a.completionEta : '').localeCompare((b.completionEta ? b.completionEta : '')));
          } else {
            items.sort((a: AdhocMaintenanceItem, b: AdhocMaintenanceItem) => (b.completionEta ? b.completionEta : '').localeCompare((a.completionEta ? a.completionEta : '')));
          }
          break;
      }

      return items;
    }

    async beforeMount() {
      await VehicleService.listActive(true);
      await VehicleService.listTypes(false);
      this.listAll();
    }

    getOneWeekPast() {
      return dayjs().add(-1, 'week').format('YYYY-MM-DD');
    }

    async listAll() {
      try {
        this.isLoading = true;
        const response: string = await AdhocMaintenanceService.listAll(true);

        if (response.length > 0 && response !== 'No Data Found') {
          AppService.errorHandler(response);
        }

      } finally {
        this.isLoading = false;
      }
    }

    allowAddNewFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
        { type: 'Workshop User', level: 1},
      ]);
    }

    allowEditFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2 || 3},
        { type: 'Workshop User', level: 1},
      ]);
    }

    allowDeleteFunctionality() {
      return this.$store.getters['app/allowAccess'](this.$store.getters['users/loggedInUser'], [
        { type: 'Super User', level: 2},
        { type: 'Operator', level: 2},
        { type: 'Workshop User', level: 1},
      ]);
    }
  // #endregion

  // #region Functions required for the Item Dialog for Create
    selectVehicle1(selectedVehicle?: Vehicle) {
      if (selectedVehicle) {
        this.selectedVehicle1 = selectedVehicle;
        this.selectVehicle2();
        this.disableLinkedTrailer = false;
      } else {
        // likely was cleared so is now undefined
        this.selectedVehicle1 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
        this.selectedVehicle2 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
      }

      this.selectedVehicle2 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
    }

    getAvailableTrailers() {
      // reset back to ALL trailers for Trailer2
      let availableRearTrailers = this.$store.getters['vehicles/vehiclesByType']('Trailer');

      availableRearTrailers = availableRearTrailers.filter((item: Vehicle) => !item.suspended); // hide the suspended vehicles on this screen
      availableRearTrailers = availableRearTrailers.filter((item: Vehicle) => !item.isLinked || (item.isLinked && !item.isFront && item.id === this.selectedVehicle1.linkedTrailerId)  ); // hide the other linked trailers

      if (this.selectedVehicle1.isLinked) {
        this.disableLinkedTrailer = true;
        var index = availableRearTrailers.findIndex((item: Vehicle) => item.id === this.selectedVehicle1.linkedTrailerId);
        if (index >= 0) {
          this.selectVehicle2(availableRearTrailers[index]);
        }
      }

      return availableRearTrailers.filter((item: Vehicle) => item.id !== this.selectedVehicle1.id);
    }

    selectVehicle2(selectedVehicle?: Vehicle) {
      if (selectedVehicle) {
        this.selectedVehicle2 = selectedVehicle;
      } else {
        // was cleared
        this.selectedVehicle2 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
      }
    }

    async launchNewItemDialog() {
      this.clickedItem = {
        id: 0,
        dateReported: dayjs().format('YYYY-MM-DD'),
        vehicle1Id: 0,
        vehicle1Code: '',
        vehicle1TypeId: 0,
        vehicle1Type: '',
        description: '',
      };

      // filter horse / trailer / front linked trailers ONLY
      this.activeVehicles = this.$store.getters['vehicles/activeVehicles'];
      this.activeVehicles = this.activeVehicles.filter((item: Vehicle) => !item.suspended); // hide the suspended vehicles on this screen
      this.activeVehicles = this.activeVehicles.filter((item: Vehicle) => !item.isLinked || (item.isLinked && item.isFront) ); // hide the rear links 

      this.selectedVehicle1 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);
      this.selectedVehicle2 = Object.assign({}, this.$store.getters['vehicles/emptyVehicle']);

      this.isUpdate = false;
      this.valid = false;
      this.title = 'Create New Adhoc Maintenance Item';

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showItemDialog = true;
    }

    launchUpdateItemDialog(item: AdhocMaintenanceItem) {
      this.clickedItem = Object.assign({}, item);

      // filter horse / trailer / front linked trailers ONLY
      this.activeVehicles = this.$store.getters['vehicles/activeVehicles'];
      this.activeVehicles = this.activeVehicles.filter((item: Vehicle) => !item.suspended); // hide the suspended vehicles on this screen
      this.activeVehicles = this.activeVehicles.filter((item: Vehicle) => !item.isLinked || (item.isLinked && item.isFront) ); // hide the rear links 

      this.selectedVehicle1 = Object.assign({}, this.$store.getters['vehicles/activeVehicles'].find((item: Vehicle) => item.id === this.clickedItem.vehicle1Id));
      this.selectedVehicle2 = Object.assign({}, this.$store.getters['vehicles/activeVehicles'].find((item: Vehicle) => item.id === this.clickedItem.vehicle2Id));
      
      this.isUpdate = true;

      this.title = 'Update Description';

      if (this.$refs.itemForm) {
        (this.$refs.itemForm as any).resetValidation();
      }

      this.showItemDialog = true;
    }

    doCreateUpdate() {
      if (this.clickedItem.id <= 0) {
        this.doCreate();
      } else {
        this.doUpdate();
      }
    }

    async doCreate() {
      try {
        this.clicked = true;

        const response = await AdhocMaintenanceService.create({
          dateReported: this.clickedItem.dateReported,
          vehicle1Id: this.selectedVehicle1.id,
          vehicle2Id: this.selectedVehicle2 && this.selectedVehicle2.id > 0 ? this.selectedVehicle2.id : undefined,
          description: this.clickedItem.description,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('AdhocMaintenance.vue - doCreate - 616 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }

    async doUpdate() {
      try {
        this.clicked = true;

        const response = await AdhocMaintenanceService.updateDescription({
          id: this.clickedItem.id,
          description: this.clickedItem.description,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showItemDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('AdhocMaintenance.vue - doUpdate - 651 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.clicked = false;
      }
    }
  // #endregion

  // #region Functions required for the Confirm Action Dialog for Delete
  async launchDeleteItemDialog(item: AdhocMaintenanceItem) {
      this.clickedItem = Object.assign({}, item);

      this.action = 'DELETE';
      this.dialogContent = 'You are about to <span style="font-weight: 700;color: #C01212">delete</span> ' 
        + ' the Adhoc Maintenance for ' + this.clickedItem.vehicle1Code + (this.clickedItem.vehicle2Code ? ' and ' + this.clickedItem.vehicle2Code : '') + '.';
      this.showConfirmActionDialog = true;
    }

    ConfirmActionDialog_cancel() {
      this.showConfirmActionDialog = false;
    }

    async ConfirmActionDialog_doConfirmed() {
      if (this.action === 'DELETE') {
        await this.doDelete();
      }
    }

    async doDelete() {
      try {
        this.confirmActionDelay = true;

        const response = await AdhocMaintenanceService.delete({
          id: this.clickedItem.id,
        });

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
          } else {
            AppService.successHandler(response.data.message);
            await this.listAll();
            this.showConfirmActionDialog = false;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('AdhocMaintenance.vue - doDelete - 706 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }

      } finally {
        this.confirmActionDelay = false;
      }
    }
  // #endregion
}
